import { useCallback, useEffect, useRef } from 'react';

/**
 * Shared plumbing for the fleet screens.
 *
 * `apiMessage`, `useBranches`, `formatDateTime` and `useQueryParams` are the
 * Operations screens' and are re-exported rather than re-written: a second
 * `apiMessage` that unwraps `ValidationPipe`'s `string[]` slightly differently
 * is exactly the kind of drift that makes one screen show a refusal and the
 * next show "action failed".
 */
export {
  apiMessage,
  formatDateTime,
  formatTime,
  useBranches,
  useNow,
  useQueryParams,
  type BranchOption,
} from '@/Pages/Operations/shared';

/**
 * ## Why this polls instead of subscribing
 *
 * The house rule is to use the realtime hooks rather than poll — and the fleet
 * epic genuinely emits the right events: `print_job.failed`, `print_job.dead`,
 * `device.revoked` and `device.unsupported_version` are all in the backend's
 * `VENUE_EVENTS`.
 *
 * They do not reach a browser. `RealtimeService` is the only thing that turns a
 * `VENUE_EVENTS` emission into a socket broadcast, and on the epic branch it
 * carries no `@OnEvent` handler for any of those four — they are emitted on the
 * in-process `EventEmitter2` and stop there. `useRealtime('print_job.failed')`
 * would join a branch room and wait forever, which is the silent-subscription
 * failure that hook's own doc comment was written about.
 *
 * So the spool polls, on an interval, and this is marked as the thing to delete
 * the moment the server starts forwarding those events. Two properties make the
 * polling honest rather than merely tolerable:
 *
 *  - it **stops while the tab is hidden**, so a dashboard left open on a back
 *    office screen overnight is not a request every ten seconds until morning;
 *  - it **skips a tick while one is still in flight**, so a slow API cannot
 *    stack requests on a loaded machine.
 */
export function useAutoRefresh(
  reload: () => void,
  { enabled, intervalMs }: { enabled: boolean; intervalMs: number },
) {
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const inFlight = useRef(false);

  const tick = useCallback(() => {
    if (inFlight.current) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    inFlight.current = true;
    try {
      reloadRef.current();
    } finally {
      // `reload` is fire-and-forget in `useApi`, so the guard is released on the
      // next macrotask rather than on a promise nobody hands back.
      window.setTimeout(() => {
        inFlight.current = false;
      }, 0);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(tick, intervalMs);
    return () => window.clearInterval(id);
  }, [enabled, intervalMs, tick]);
}

/** How long ago, in whole minutes — the unit a "last seen" column is read in. */
export function minutesSince(iso: string | null | undefined, now: number = Date.now()): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((now - then) / 60_000));
}

/**
 * A stable idempotency key for one operator gesture.
 *
 * Reprint and test print both **require** one (ADR-0009 §5): a double-tapped
 * button or a client retrying a timed-out POST carries the same key and
 * collapses to one copy, and a reprint with no key is a 400 — because a
 * silently doubled kitchen ticket is worse than a refused button.
 */
export function operationKey(prefix: string): string {
  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}:${random}`;
}
